<div
  class="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4 font-sans antialiased"
>
  <div class="bg-white rounded-xl shadow-xl p-8 w-full max-w-lg">
    <!-- Header -->
    <div class="text-center mb-8">
      <h2 class="text-3xl font-bold text-red-700 mb-2">Delete Account</h2>
      <p class="text-gray-600">This action cannot be undone</p>
    </div>

    <!-- Warning Section (always visible) -->
    <!-- Updated Warning Section with mat-icon -->
    <div
      class="bg-red-50 border-l-4 border-red-500 text-red-800 p-4 rounded-lg mb-6"
      role="alert"
    >
      <div class="flex items-start">
        <mat-icon class="text-red-500 mr-3 mt-0.5">warning</mat-icon>
        <div>
          <p class="font-bold text-sm">Warning!</p>
          <p class="text-sm mt-1">
            All your data, posts, and account information will be permanently
            deleted and cannot be recovered.
          </p>
        </div>
      </div>
    </div>

    <!-- Message Display (global) -->
    <!-- Updated Message Display with mat-icons -->
    <div
      *ngIf="message"
      class="mb-6 p-4 rounded-lg text-center transition-all duration-300"
      [ngClass]="{
        'bg-green-100 text-green-700 border border-green-200':
          messageType === 'success',
        'bg-red-100 text-red-700 border border-red-200': messageType === 'error'
      }"
    >
      <div class="flex items-center justify-center">
        <mat-icon *ngIf="messageType === 'success'" class="mr-2"
          >check_circle</mat-icon
        >
        <mat-icon *ngIf="messageType === 'error'" class="mr-2">error</mat-icon>
        {{ message }}
      </div>
    </div>

    <!-- Step 1: Initial State -->
    <!-- Updated Initial State with mat-icon -->
    <div *ngIf="currentStep === 'initial'" class="space-y-6">
      <div class="text-center space-y-4">
        <div
          class="mx-auto w-20 h-20 bg-red-100 rounded-full flex items-center justify-center"
        >
          <mat-icon class="text-red-600 text-4xl">delete</mat-icon>
        </div>
        <p class="text-gray-700 text-lg">Ready to delete your account?</p>
        <p class="text-gray-500 text-sm">
          We'll need to verify your identity first
        </p>
      </div>

      <button
        class="w-full bg-red-600 text-white font-semibold py-4 rounded-lg hover:bg-red-700 focus:ring-4 focus:ring-red-200 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
        (click)="handleDeleteAccount()"
      >
        Proceed to Delete Account
      </button>
    </div>

    <!-- Step 2: Phone Verification -->
    <div *ngIf="currentStep === 'verification'" class="space-y-6">
      <div class="text-center mb-6">
        <h3 class="text-xl font-semibold text-gray-800 mb-2">
          Verify Your Account
        </h3>
        <p class="text-gray-600">Enter your registered phone number</p>
      </div>

      <form [formGroup]="verificationFormGroup" class="space-y-4">
        <!-- Phone Number Input -->
        <div class="space-y-2">
          <label class="block text-sm font-medium text-gray-700"
            >Phone Number *
          </label>
          <div class="relative">
            <mat-form-field
              style="width: 100%"
              appearance="outline"
              subscriptSizing="dynamic"
              hideRequiredMarker
            >
              <ngx-mat-intl-tel-input
                #phoneInput
                [enablePlaceholder]="true"
                [preferredCountries]="['in']"
                [enableSearch]="true"
                name="phone"
                formControlName="mobile"
                [label]="'Mobile Number'"
                [maxlength]="15"
              ></ngx-mat-intl-tel-input>
            </mat-form-field>

            <!-- Loading State -->
            <div
              *ngIf="isLoading"
              class="text-blue-600 text-sm mt-1 flex items-center"
            >
              <mat-icon class="animate-spin mr-2">refresh</mat-icon>
              Checking organizations...
            </div>

            <!-- No Organizations Found -->
            <div
              *ngIf="
                isMobileValid && !isMobileExists && !isLoading && hasVerified
              "
              class="text-red-600 text-sm mt-1"
            >
              No organizations found for this phone number
            </div>

            <!-- Organizations Found -->
            <div
              *ngIf="isMobileExists && hasVerified"
              class="text-green-600 text-sm mt-1"
            >
              Organizations found for this number
            </div>
          </div>

          <!-- Verify Button -->
          <button
            type="button"
            class="w-full bg-blue-600 text-white font-medium py-3 rounded-lg hover:bg-blue-700 transition-all duration-200 focus:ring-4 focus:ring-blue-200 disabled:opacity-50 disabled:cursor-not-allowed mt-3"
            [disabled]="!mobileControl?.valid || isLoading"
            (click)="verifyMobileNumber()"
          >
            <span *ngIf="!isLoading">Verify Mobile Number</span>
            <span *ngIf="isLoading" class="flex items-center justify-center">
              <mat-icon class="animate-spin mr-2">refresh</mat-icon>
              Verifying...
            </span>
          </button>
        </div>

        <!-- Organization Selection -->
        <div
          *ngIf="isMobileExists && organizations.length > 0"
          class="space-y-2"
        >
          <label class="block text-sm font-medium text-gray-700"
            >Select Organization *</label
          >
          <div class="relative">
            <!-- Input field -->
            <input
              type="text"
              [(ngModel)]="orgSearchTerm"
              [ngModelOptions]="{ standalone: true }"
              placeholder="Type to search organizations"
              (input)="onOrganizationInput($event)"
              class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-red-500 focus:border-red-500 transition-all duration-200"
              [class.border-red-500]="
                organizations.length > 0 && !selectedOrganization
              "
              [class.border-green-500]="selectedOrganization"
            />

            <!-- Dropdown with responsive positioning -->
            <div
              *ngIf="filteredOrgs.length > 0 && !selectedOrganization"
              class="absolute z-10 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto w-full bottom-full mb-1 md:w-80 md:left-full md:top-0 md:bottom-auto md:mb-0 md:ml-2"
            >
              <div
                *ngFor="let org of filteredOrgs"
                class="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                (click)="selectOrganization(org)"
              >
                {{ org.organizationName }}
              </div>
            </div>

            <!-- Selected organization display -->
            <div
              *ngIf="selectedOrganization"
              class="text-green-600 text-sm mt-1"
            >
              Selected: {{ selectedOrganization.organizationName }}
            </div>
          </div>
        </div>
      </form>

      <div class="flex space-x-4">
        <button
          type="button"
          class="flex-1 bg-gray-300 text-gray-700 font-medium py-3 rounded-lg hover:bg-gray-400 transition-all duration-200 focus:ring-4 focus:ring-gray-200"
          (click)="goBack()"
        >
          Back
        </button>
        <!-- Updated Send OTP Button with mat-icon -->
        <button
          type="button"
          class="flex-1 bg-red-600 text-white font-medium py-3 rounded-lg hover:bg-red-700 transition-all duration-200 focus:ring-4 focus:ring-red-200 disabled:opacity-50 disabled:cursor-not-allowed"
          [disabled]="
            !isMobileExists ||
            isSendingOtp ||
            !selectedOrganization ||
            !hasVerified
          "
          (click)="sendOtpForVerification()"
        >
          <span *ngIf="!isSendingOtp">Send OTP</span>
          <span *ngIf="isSendingOtp" class="flex items-center justify-center">
            <mat-icon class="animate-spin mr-2">refresh</mat-icon>
            Sending...
          </span>
        </button>
      </div>
    </div>

    <!-- Step 3: OTP Verification -->
    <div *ngIf="currentStep === 'otp'" class="space-y-6">
      <div class="text-center mb-6">
        <h3 class="text-xl font-semibold text-gray-800 mb-2">Enter OTP</h3>
        <p class="text-gray-600">
          We've sent a code to
          <span class="font-medium">{{ phoneNumber }}</span>
        </p>
      </div>

      <form [formGroup]="verificationFormGroup" class="space-y-4">
        <div class="space-y-2">
          <label class="block text-sm font-medium text-gray-700"
            >OTP Code *</label
          >
          <input
            type="text"
            formControlName="otp"
            placeholder="Enter 4-6 digit code"
            maxlength="6"
            class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-red-500 focus:border-red-500 transition-all duration-200 text-center text-lg tracking-widest"
            [class.border-red-500]="otpControl?.invalid && otpControl?.touched"
          />
          <div
            *ngIf="otpControl?.invalid && otpControl?.touched"
            class="text-red-600 text-sm"
          >
            Please enter a valid OTP code
          </div>
        </div>
      </form>

      <!-- Resend OTP -->
      <div class="text-center">
        <button
          *ngIf="resendCountdown <= 0"
          class="text-red-600 hover:text-red-700 font-medium text-sm underline"
          (click)="resendOtp()"
          [disabled]="isSendingOtp"
        >
          Resend OTP
        </button>
        <p *ngIf="resendCountdown > 0" class="text-gray-500 text-sm">
          Resend OTP in {{ resendCountdown }}s
        </p>
      </div>

      <div class="flex space-x-4">
        <button
          type="button"
          class="flex-1 bg-gray-300 text-gray-700 font-medium py-3 rounded-lg hover:bg-gray-400 transition-all duration-200 focus:ring-4 focus:ring-gray-200"
          (click)="goBack()"
        >
          Back
        </button>
        <!-- Updated Verify OTP Button with mat-icon -->
        <button
          type="button"
          class="flex-1 bg-red-600 text-white font-medium py-3 rounded-lg hover:bg-red-700 transition-all duration-200 focus:ring-4 focus:ring-red-200 disabled:opacity-50 disabled:cursor-not-allowed"
          [disabled]="!otpControl?.valid || isVerifyingOtp"
          (click)="verifyOtp()"
        >
          <span *ngIf="!isVerifyingOtp">Verify OTP</span>
          <span *ngIf="isVerifyingOtp" class="flex items-center justify-center">
            <mat-icon class="animate-spin mr-2">refresh</mat-icon>
            Verifying...
          </span>
        </button>
      </div>
    </div>

    <!-- Complete the Step 4: Deletion Policy Selection section -->
    <div *ngIf="currentStep === 'policy'" class="space-y-6">
      <div class="text-center mb-6">
        <h3 class="text-xl font-semibold text-gray-800 mb-2">
          Choose Deletion Policy
        </h3>
        <p class="text-gray-600">
          Select when you want your account to be deleted
        </p>
      </div>

      <div class="space-y-4">
        <div
          class="border border-gray-300 rounded-lg p-4 cursor-pointer transition-all duration-200 hover:border-red-500 hover:bg-red-50 hover:shadow-md"
          [class.border-red-500]="selectedDeletionPolicy === 'immediate'"
          [class.bg-red-50]="selectedDeletionPolicy === 'immediate'"
          [class.shadow-md]="selectedDeletionPolicy === 'immediate'"
          (click)="selectDeletionPolicy('immediate')"
        >
          <div class="flex items-center space-x-3">
            <input
              type="radio"
              name="deletionPolicy"
              value="immediate"
              [(ngModel)]="selectedDeletionPolicy"
              [ngModelOptions]="{ standalone: true }"
              class="text-red-600 focus:ring-red-500 cursor-pointer"
              (click)="$event.stopPropagation()"
            />
            <div class="flex-1">
              <h4
                class="font-medium text-gray-800 transition-colors duration-200"
              >
                Delete Immediately
              </h4>
              <p class="text-sm text-gray-600 transition-colors duration-200">
                Your account will be deleted right away
              </p>
            </div>
          </div>
        </div>

        <div
          class="border border-gray-300 rounded-lg p-4 cursor-pointer transition-all duration-200 hover:border-red-500 hover:bg-red-50 hover:shadow-md"
          [class.border-red-500]="selectedDeletionPolicy === '30days'"
          [class.bg-red-50]="selectedDeletionPolicy === '30days'"
          [class.shadow-md]="selectedDeletionPolicy === '30days'"
          (click)="selectDeletionPolicy('30days')"
        >
          <div class="flex items-center space-x-3">
            <input
              type="radio"
              name="deletionPolicy"
              value="30days"
              [(ngModel)]="selectedDeletionPolicy"
              [ngModelOptions]="{ standalone: true }"
              class="text-red-600 focus:ring-red-500 cursor-pointer"
              (click)="$event.stopPropagation()"
            />
            <div class="flex-1">
              <h4
                class="font-medium text-gray-800 transition-colors duration-200"
              >
                Delete in 30 Days
              </h4>
              <p class="text-sm text-gray-600 transition-colors duration-200">
                Grace period of 30 days to recover your account
              </p>
            </div>
          </div>
        </div>
      </div>

      <div class="flex space-x-4">
        <button
          type="button"
          class="flex-1 bg-gray-300 text-gray-700 font-medium py-3 rounded-lg hover:bg-gray-400 transition-all duration-200 focus:ring-4 focus:ring-gray-200"
          (click)="goBack()"
        >
          Back
        </button>
        <button
          type="button"
          class="flex-1 bg-red-600 text-white font-medium py-3 rounded-lg hover:bg-red-700 transition-all duration-200 focus:ring-4 focus:ring-red-200"
          [disabled]="!selectedDeletionPolicy"
          [class.opacity-50]="!selectedDeletionPolicy"
          [class.cursor-not-allowed]="!selectedDeletionPolicy"
          (click)="currentStep = 'confirmation'"
        >
          Continue
        </button>
      </div>
    </div>

    <!-- Step 5: Final Confirmation -->
    <div *ngIf="currentStep === 'confirmation'" class="space-y-6">
      <div class="text-center mb-6">
        <div
          class="mx-auto w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mb-4"
        >
          <mat-icon class="text-red-600 text-4xl">warning</mat-icon>
        </div>
        <h3 class="text-xl font-semibold text-gray-800 mb-2">
          Final Confirmation
        </h3>
        <p class="text-gray-600">
          This action is irreversible. Your account will be permanently deleted.
        </p>
      </div>

      <!-- Account Details Summary -->
      <div class="bg-gray-50 rounded-lg p-4 space-y-2">
        <div class="flex justify-between items-center">
          <span class="text-gray-600">Phone Number:</span>
          <span class="font-medium">{{ phoneNumber }}</span>
        </div>
        <div
          *ngIf="selectedOrganization"
          class="flex justify-between items-center"
        >
          <span class="text-gray-600">Organization:</span>
          <span class="font-medium">{{
            selectedOrganization.organizationName
          }}</span>
        </div>
      </div>

      <!-- Final Warning -->
      <div class="bg-red-50 border border-red-200 rounded-lg p-4">
        <div class="flex items-start">
          <mat-icon class="text-red-500 mr-3 mt-0.5">error</mat-icon>
          <div>
            <p class="font-bold text-red-800 text-sm">Last Warning!</p>
            <p class="text-red-700 text-sm mt-1">
              Once deleted, your account cannot be recovered. All your data will
              be permanently lost.
            </p>
          </div>
        </div>
      </div>

      <!-- Confirmation Input -->
      <div class="space-y-2">
        <label class="block text-sm font-medium text-gray-700">
          Type <strong>DELETE</strong> to confirm account deletion
        </label>
        <input
          type="text"
          [(ngModel)]="confirmationText"
          placeholder="Type DELETE to confirm"
          class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-red-500 focus:border-red-500 transition-all duration-200 text-sm"
          [class.border-red-500]="
            confirmationText && confirmationText !== 'DELETE'
          "
          [class.border-green-500]="confirmationText === 'DELETE'"
        />
        <div
          *ngIf="confirmationText && confirmationText !== 'DELETE'"
          class="text-red-600 text-sm"
        >
          Please type "DELETE" exactly as shown
        </div>
      </div>
      <!-- (click)="cancelDeletion()" -->
      <div class="flex space-x-4">
        <button
          type="button"
          class="flex-1 bg-gray-300 text-gray-700 font-medium py-3 rounded-lg hover:bg-gray-400 transition-all duration-200 focus:ring-4 focus:ring-gray-200"
        >
          <!-- <mat-icon class="mr-2">close</mat-icon> -->
          Cancel
        </button>
        <button
          type="button"
          class="flex-1 bg-red-600 text-white font-medium py-3 rounded-lg hover:bg-red-700 transition-all duration-200 focus:ring-4 focus:ring-red-200 disabled:opacity-50 disabled:cursor-not-allowed"
          [disabled]="confirmationText !== 'DELETE' || isDeletingAccount"
          (click)="confirmDeleteAccount()"
        >
          <span
            *ngIf="!isDeletingAccount"
            class="flex items-center justify-center"
          >
            <!-- <mat-icon class="mr-2">delete_forever</mat-icon> -->
            Delete Account Permanently
          </span>
          <span
            *ngIf="isDeletingAccount"
            class="flex items-center justify-center"
          >
            <mat-icon class="animate-spin mr-2">refresh</mat-icon>
            Deleting Account...
          </span>
        </button>
      </div>
    </div>
  </div>
</div>
